import React, { useEffect, useState } from "react";
import ReactDOM from 'react-dom';
import { DateTime } from 'luxon';
import { Clock, ClockGranularity } from "../clock";
import { DensityMapControls } from "../density-map-controls";
import { DensityMapControlsEventType } from "./event-type";
import { MapControlTrojan } from './map-control-trojan';


type Props = {
  dateRange: { 
    from: string; 
    to: string;
  };
  onAutopilotUpdate?: (msUntilNextUpdate: number) => void;
  onTimelineUpdate?: (timestamp?: string) => void;
}


const defaults: Required<Pick<Props, 'onAutopilotUpdate' | 'onTimelineUpdate'>> = {
  onAutopilotUpdate: (msUntilNextUpdate) => console.log(`onAutopilotpdate not set (next update in ${msUntilNextUpdate} ms)`),
  onTimelineUpdate: (timestamp) => console.log(`onTimelineUpdate not set ${timestamp && ` (timestamp ${timestamp})`}`),
}

export const useDensityMapControls = (props: Props) => {
  
  const allProps: Required<Props> = {...defaults, ...props};
  const millisecondsPerUpdate = 500;
  const granularity: ClockGranularity = 'hour';
  const { dateRange: {from, to}, onAutopilotUpdate, onTimelineUpdate } = allProps;
  const densityMapControls = new MapControlTrojan();
  
  const [playing, setPlaying] = useState(false);
  const [playbackRepeat, setPlaybackRepeat] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [autopilotIsOn, setAutopilotIsOn] = useState(false);
  const [wrapper] = useState<HTMLElement>(densityMapControls.getContainer());
  const [currentTime, setCurrentTime] = useState<DateTime>();
  
  useEffect(() => {
    wrapper && ReactDOM.render(wrappedComponent, wrapper);
  });

  const clock = new Clock({ from, to, granularity});

  const handleControlPanelEvent = (event: CustomEvent) => {
    switch (event.type) {
      case DensityMapControlsEventType.TOGGLE_AUTOPILOT:
        setAutopilotIsOn(autopilotIsOn => !autopilotIsOn);
        break;
      case DensityMapControlsEventType.TOGGLE_PLAYING:
        setPlaying(playing => !playing);
        break;
      case DensityMapControlsEventType.SET_PLAYBACK_SPEED:
        setPlaybackSpeed(event.detail.value);
        break;
      case DensityMapControlsEventType.TOGGLE_PLAYBACK_REPEAT:
        setPlaybackRepeat(playbackRepeat => !playbackRepeat);
        break;
    }
  };

   // autopilot toggle and update handler
   useEffect(() => {
    if (!autopilotIsOn) return onAutopilotUpdate(0);

    updateAutopilot();
    const interval = setInterval(updateAutopilot, millisecondsPerUpdate);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autopilotIsOn])
  
  const updateAutopilot = () => {
    onAutopilotUpdate(millisecondsPerUpdate);
  }

  // toggle timeline playback and tick handler
  useEffect(() => {
    const callback = playing ? tick : () => null;
    const interval = setInterval(callback, millisecondsPerUpdate / playbackSpeed);
    // playing && onTimelineUpdate();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, playbackSpeed]);

  const tick = () => {
    const stopAfterUpdate = clock.isAtEnd && !playbackRepeat;
    onTimelineUpdate(clock.now.toISO({ includeOffset: false }))
    clock.isAtEnd ? clock.toStart() : clock.increment();
    setCurrentTime(clock.now);
    if (stopAfterUpdate) setPlaying(false);
  };
  
  /**
   * The DensityMapControls component will be rendered into a class implementing mapboxgl.IControl
   * RThe IControl implementor can then be added as a custom control layer to a mapbox map using
   * the map.addControl() method.
   */
   const wrappedComponent = (
     <DensityMapControls
       dateRange={{from:clock.from, to:clock.to}}
       currentTime={currentTime}
       clock={clock}
       isPlaying={playing}
       isRepeating={playbackRepeat}
       playbackSpeed={playbackSpeed}
       baseSpeed={millisecondsPerUpdate}
       eventHandler={handleControlPanelEvent}
       autopilotIsOn={autopilotIsOn}
     >
       <DensityMapControls.DateTimeLabel />
       <DensityMapControls.PlaybackControls />
       <DensityMapControls.Autopilot />
     </DensityMapControls>
  );

  return { densityMapControls }
}